import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { AllocationField } from './AllocationField';
import { SlaBurndown } from './SlaBurndown';
import { SummaryBarProps } from './SummaryBar.props';
import { SummaryBarActions } from './SummaryBarActions';
import { COLORS } from '../../../configs/colors';
import { PriorityDot } from '../../atoms/PriorityDot';
import { StatusBadge } from '../../atoms/StatusBadge';

/** Walk hierarchy to find the outlet (physical location) name. */
const findOutletName = (node: unknown): string => {
  if (!node || typeof node !== 'object') return '';
  const n = node as { nodeTypeCode?: string; name?: string; children?: unknown[] };
  if (n.nodeTypeCode === 'outlet') return n.name ?? '';
  for (const child of n.children ?? []) {
    const found = findOutletName(child);
    if (found) return found;
  }
  return '';
};

const Divider: React.FC = () => (
  <Box sx={{ width: '1px', height: 24, bgcolor: 'v4.border', flexShrink: 0 }} />
);

/** Tag colours — rotate through a fixed palette from COLORS config. */
const TAG_COLORS = [
  { bg: COLORS.TagBlueBg, text: COLORS.TagBlueText },
  { bg: COLORS.TagGreenBg, text: COLORS.TagGreenText },
  { bg: COLORS.TagAmberBg, text: COLORS.TagAmberText },
  { bg: COLORS.TagPurpleBg, text: COLORS.TagPurpleText },
  { bg: COLORS.TagRedBg, text: COLORS.TagRedText },
  { bg: COLORS.TagIndigoBg, text: COLORS.TagIndigoText },
];

export const SummaryBar: React.FC<SummaryBarProps> = ({
  service,
  logoPic,
  onStatusChange,
  onAllocate,
  onPartAction,
  onTask,
}) => {
  const statusName = service?.serviceStatus?.name ?? '';
  const priorityLabel = service?.priorityLabel ?? service?.priorityCode ?? '';
  const technicianName = service?.primaryAllocation?.node?.name ?? '';
  const locationName = findOutletName(service?.locationHierarchy) || (service?.mainLocation ?? '');
  const contractName = service?.contract ?? '';
  const tags = service?.tags ?? [];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'v4.border',
        borderRadius: '8px',
        px: 2,
        py: 1,
        boxSizing: 'border-box',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Left section — fills space, shrinks to make room for buttons */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
        {logoPic ? (
          <Tooltip title={contractName} placement="top" arrow>
            <Box component="img" src={logoPic} alt={contractName} sx={{ height: 32, maxWidth: 120, objectFit: 'contain', flexShrink: 0 }} />
          </Tooltip>
        ) : (
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {contractName || '—'}
          </Typography>
        )}

        <Typography sx={{ fontWeight: 700, fontSize: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>
          {service?.id ?? '—'}{locationName ? `: ${locationName}` : ''}
        </Typography>

        <Divider />

        {statusName && (
          <Box component="span" sx={{ flexShrink: 0, '& > *': { fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.5 } }}>
            <StatusBadge status={statusName} />
          </Box>
        )}
        {priorityLabel && (
          <Box component="span" sx={{ flexShrink: 0, '& > *': { fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.5 } }}>
            <PriorityDot priority={priorityLabel} />
          </Box>
        )}

        <Divider />

        <AllocationField
          label="Technician"
          value={technicianName}
        />

        {tags.length > 0 && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden', flexShrink: 1 }}>
              {tags.map((tag, idx) => {
                const colors = TAG_COLORS[idx % TAG_COLORS.length];
                return (
                  <Chip
                    key={tag.text ?? idx}
                    label={tag.text}
                    size="small"
                    sx={{ height: 20, fontSize: '10px', fontWeight: 600, bgcolor: colors.bg, color: colors.text }}
                  />
                );
              })}
            </Box>
          </>
        )}
        {/* SLA burndown bars */}
        {(service?.clientSLA || service?.opsSLA) && <Divider />}
        <SlaBurndown label="Client SLA" sla={service?.clientSLA} />
        <SlaBurndown label="Ops SLA" sla={service?.opsSLA} />
      </Box>

      {/* Right section — action buttons, right-aligned, never shrink */}
      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, ml: 'auto' }}>
        <SummaryBarActions onStatusChange={onStatusChange} onAllocate={onAllocate} onPartAction={onPartAction} onTask={onTask} />
      </Box>
    </Box>
  );
};
